import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/lib/page-header'
import { format } from 'date-fns'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChartContainer, type ChartConfig } from '@/components/ui/chart'
import { usePrimoData, generateHistory, formatEUR, PRICE_LEVELS } from '@/lib/primodata'
import { usePriceFilters, useFilteredData } from '@/lib/price-filters'

export const Route = createFileRoute('/_shell/historik')({
  component: HistorikPage,
})

const CHART_COLORS = [
  'var(--color-chart-1)',
  'var(--color-chart-2)',
  'var(--color-chart-3)',
  'var(--color-chart-4)',
  'var(--color-chart-5)',
]

const MAX_COUNTRIES = 15

function HistoryLineChart({ drug }: { drug: string }) {
  const { data } = usePrimoData()
  const { priceLevel, selectedCountries } = usePriceFilters()
  const priceLevelLabel = PRICE_LEVELS.find(l => l.value === priceLevel)?.label ?? priceLevel

  // Generate history points for the chosen drug
  const historyData = useMemo(() => {
    if (!data) return []
    const records = data.filter(r => {
      if (r['Global Product Name'] !== drug) return false
      if (selectedCountries.length > 0 && !selectedCountries.includes(r.Country)) return false
      return true
    })
    return generateHistory(records, priceLevel)
  }, [data, drug, priceLevel, selectedCountries])

  // Pivot: one row per YYYY-MM, one column per country
  const { pivotedData, countries } = useMemo(() => {
    const allCountries = [...new Set(historyData.map(p => p.country))].sort()

    // Cap to MAX_COUNTRIES: prefer filter selection, otherwise highest-priced
    const priceByCountry = new Map<string, number>()
    for (const p of historyData) {
      priceByCountry.set(p.country, Math.max(priceByCountry.get(p.country) ?? 0, p.price))
    }
    const countries = allCountries
      .sort((a, b) => (priceByCountry.get(b) ?? 0) - (priceByCountry.get(a) ?? 0))
      .slice(0, MAX_COUNTRIES)

    // Build lookup: "YYYY-MM|country" -> price
    const lookup = new Map<string, number>()
    for (const p of historyData) {
      if (!countries.includes(p.country)) continue
      const key = `${p.date.getFullYear()}-${String(p.date.getMonth() + 1).padStart(2, '0')}|${p.country}`
      lookup.set(key, p.price)
    }

    // All unique months, sorted
    const months = [...new Set(
      historyData.map(p => `${p.date.getFullYear()}-${String(p.date.getMonth() + 1).padStart(2, '0')}`)
    )].sort()

    const pivotedData = months.map(month => {
      const row: Record<string, number | string | null> = { month }
      for (const c of countries) {
        row[c] = lookup.get(`${month}|${c}`) ?? null
      }
      return row
    })

    return { pivotedData, countries }
  }, [historyData])

  const formatY = (v: number) =>
    v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` :
    v >= 1_000 ? `${(v / 1_000).toFixed(0)}K` : String(v)

  const formatMonth = (v: string) => {
    try { return format(new Date(`${v}-01`), 'MMM yy') } catch { return v }
  }

  const chartConfig = {} satisfies ChartConfig

  if (!pivotedData.length) return (
    <p className="py-12 text-center text-muted-foreground">
      Ingen data for valgte filtre.
    </p>
  )

  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <ChartContainer config={chartConfig} className="h-[420px]">
        <LineChart data={pivotedData} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickFormatter={formatMonth}
            tick={{ fontSize: 11 }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            tickFormatter={formatY}
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              const sorted = [...payload].sort((a, b) => (b.value as number) - (a.value as number))
              return (
                <div className="rounded-lg border bg-background p-3 shadow-md text-xs space-y-1.5 min-w-[180px] max-h-64 overflow-y-auto">
                  <p className="font-semibold">{formatMonth(label as string)}</p>
                  {sorted.filter(p => p.value != null).map(p => (
                    <div key={String(p.dataKey)} className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: p.stroke }} />
                        {String(p.dataKey)}
                      </span>
                      <span className="font-mono font-medium">{formatEUR(p.value as number)}</span>
                    </div>
                  ))}
                </div>
              )
            }}
          />
          {countries.length <= 10 && (
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" iconSize={16} />
          )}
          {countries.map((country, i) => (
            <Line
              key={country}
              dataKey={country}
              stroke={CHART_COLORS[i % CHART_COLORS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ChartContainer>
      <p className="text-xs text-muted-foreground">
        Historiske datapunkter er syntetisk genereret · prisniveau: {priceLevelLabel}
        {countries.length === MAX_COUNTRIES && historyData.length > 0 && (
          <> · viser top {MAX_COUNTRIES} lande efter pris</>
        )}
      </p>
    </div>
  )
}

function HistorikPage() {
  const { data, isLoading } = usePrimoData()
  const filteredData = useFilteredData(data)
  const allDrugs = useMemo(
    () => [...new Set((data ?? []).map(r => r['Global Product Name']))].sort(),
    [data]
  )
  const filteredDrugs = useMemo(
    () => [...new Set(filteredData.map(r => r['Global Product Name']))].sort(),
    [filteredData]
  )

  const [selectedDrug, setSelectedDrug] = useState<string>('')
  const drugToShow = (() => {
    const pool = filteredDrugs.length > 0 ? filteredDrugs : allDrugs
    if (pool.includes(selectedDrug)) return selectedDrug
    return pool[0] ?? ''
  })()

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Prishistorik" />

      {isLoading ? (
        <p className="text-muted-foreground">Indlæser data…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Lægemiddel:</span>
            <Select value={drugToShow} onValueChange={setSelectedDrug}>
              <SelectTrigger className="h-9 w-64 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allDrugs.map(d => (
                  <SelectItem
                    key={d}
                    value={d}
                    className={`text-sm ${filteredDrugs.length > 0 && !filteredDrugs.includes(d) ? 'opacity-40' : ''}`}
                  >
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filteredDrugs.length > 0 && filteredDrugs.length < allDrugs.length && (
              <span className="text-xs text-muted-foreground">
                ({filteredDrugs.length} i aktivt filter)
              </span>
            )}
          </div>

          {drugToShow && <HistoryLineChart drug={drugToShow} />}
        </div>
      )}
    </div>
  )
}
